/**
 * Houses Component - Displays list of houses with pagination and search.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import APIAgent from '../../agents/apiAgent';
import { useFavorites } from '../../agents/favoritesAgent';
import { useTourRequests } from '../../agents/tourRequestAgent';
import { useSavedSearches } from '../../agents/notificationAgent';
import TourRequestModal from '../Tours/TourRequestModal';
import BathBreakdownBadge from '../ui/BathBreakdownBadge';
import Badge from '../ui/Badge';
import OwnershipReviewedBadge from '../OwnershipReviewedBadge/OwnershipReviewedBadge';
import SearchPanel from '../ui/SearchPanel';
import FieldError from '../ui/FieldError';
import { isSold, isTourable } from '../../utils/listingStatus';
import { findRangeErrors, hasRangeErrors } from '../../utils/rangeValidation';
import './HousesComponent.css';
import { isVacantLand, VACANT_LAND_LABEL } from '../../utils/propertyKind';

const SEARCH_MODES = {
  CRITERIA: 'criteria',
  SEMANTIC: 'semantic',
};

const PAGE_SIZE = 12;

const FEATURE_PROFILE_KEYS = [
  'exterior_features',
  'special_features',
  'recent_upgrades',
  'appliances_included',
  'flooring',
  'heating_type',
  'cooling_type',
  'pool',
  'fireplace',
  'laundry',
  'garage_type',
  'internet',
  'hoa_includes',
];

const splitFeatureText = (value) =>
  String(value)
    .split(/[,;\n•]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const extractPropertyFeatures = (house) => {
  const profile = house?.mls_profile;
  if (!profile || typeof profile !== 'object') return [];

  const features = [];

  FEATURE_PROFILE_KEYS.forEach((key) => {
    const value = profile[key];
    if (Array.isArray(value)) {
      // A "None" chip answers the question; it isn't a feature to show.
      features.push(
        ...value
          .map((item) => String(item).trim())
          .filter((item) => item && item.toLowerCase() !== 'none'),
      );
      return;
    }

    if (typeof value !== 'string') return;

    const normalized = value.trim();
    if (!normalized) return;

    if (key === 'special_features' || key === 'recent_upgrades') {
      features.push(...splitFeatureText(normalized));
      return;
    }

    const lowered = normalized.toLowerCase();
    if (['none', 'no', 'unknown', 'n/a'].includes(lowered)) return;

    features.push(normalized);
  });

  return [...new Set(features)].slice(0, 6);
};

const initialCriteria = {
  city: '',
  state: '',
  min_price: '',
  max_price: '',
  min_bedrooms: '',
  // MLS-grade-listing-data slice 1: full-bath count filter. The backend's
  // PropertyFilterCriteria.min_full_baths excludes legacy listings whose
  // `full_baths` is NULL, so this filter is strict by design.
  min_full_baths: '',
};

const HousesComponent = ({ buyerId, onViewHouse, initialSemanticQuery }) => {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);

  const [searchMode, setSearchMode] = useState(
    initialSemanticQuery ? SEARCH_MODES.SEMANTIC : SEARCH_MODES.CRITERIA
  );
  const [criteria, setCriteria] = useState(initialCriteria);
  const [semanticQuery, setSemanticQuery] = useState(initialSemanticQuery || '');
  const [activeSearch, setActiveSearch] = useState(null);
  
  const [tourModalOpen, setTourModalOpen] = useState(false);
  const [selectedHouseForTour, setSelectedHouseForTour] = useState(null);

  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const searchPanelRef = useRef(null);
  // keyset pagination: maps page number → cursor (exclusive upper bound on id)
  // page 1 always starts with null (no cursor = fetch newest records)
  const cursorMapRef = useRef({ 1: null });

  const formatCompact = (value) => {
    const num = Number(value);
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000) return Math.round(num / 1_000) + 'k';
    return String(num);
  };

  // Inverted min/max price blocks search and save (QA run 7 S3).
  const rangeErrors = useMemo(() => findRangeErrors(criteria), [criteria]);
  const criteriaInvalid = searchMode === SEARCH_MODES.CRITERIA && hasRangeErrors(rangeErrors);

  const activeFilterCount = useMemo(() => {
    if (searchMode === SEARCH_MODES.SEMANTIC) return semanticQuery.trim() ? 1 : 0;
    return Object.values(criteria).filter(v => v !== '').length;
  }, [searchMode, criteria, semanticQuery]);

  const filterChips = useMemo(() => {
    if (searchMode === SEARCH_MODES.SEMANTIC && semanticQuery.trim()) {
      const q = semanticQuery.trim();
      return [{ key: 'query', label: q.length > 20 ? q.slice(0, 20) + '...' : q }];
    }
    const chips = [];
    if (criteria.city) chips.push({ key: 'city', label: criteria.city });
    if (criteria.state) chips.push({ key: 'state', label: criteria.state });
    if (criteria.min_price) chips.push({ key: 'min_price', label: '$' + formatCompact(criteria.min_price) + '+' });
    if (criteria.max_price) chips.push({ key: 'max_price', label: '<=$' + formatCompact(criteria.max_price) });
    if (criteria.min_bedrooms) chips.push({ key: 'min_bedrooms', label: criteria.min_bedrooms + '+ bed' });
    if (criteria.min_full_baths) {
      chips.push({ key: 'min_full_baths', label: criteria.min_full_baths + '+ full' });
    }
    return chips.slice(0, 3);
  }, [searchMode, criteria, semanticQuery]);

  const handleRemoveFilter = (key) => {
    if (key === 'query') {
      setSemanticQuery('');
    } else {
      setCriteria(prev => ({ ...prev, [key]: '' }));
    }
  };

  const { addFavorite, removeFavorite, fetchFavorites, isFavorite } = useFavorites(buyerId);
  useTourRequests(buyerId);
  const { createSearch } = useSavedSearches(buyerId);

  const hasKnownTotal = Number.isFinite(totalCount);
  const totalPages = hasKnownTotal ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

  const fetchPage = useCallback(async (pageNum) => {
    try {
      setLoading(true);
      setError(null);
      const skip = (pageNum - 1) * PAGE_SIZE;

      if (activeSearch?.mode === SEARCH_MODES.SEMANTIC) {
        const data = await APIAgent.semanticSearchHouses(activeSearch.query, PAGE_SIZE);
        setHouses(data);
        setTotalCount(data.length);
        setHasNextPage(false);
      } else if (activeSearch?.mode === SEARCH_MODES.CRITERIA) {
        const result = await APIAgent.getHousesByCriteria(activeSearch.criteria, skip, PAGE_SIZE);
        setHouses(result.houses);
        setTotalCount(result.total_count);
        setHasNextPage(false);
      } else {
        const cursor = cursorMapRef.current[pageNum] ?? null;
        const result = await APIAgent.getHouses(cursor, PAGE_SIZE);
        setHouses(result.houses);
        setTotalCount(Number.isFinite(result.total_count) ? result.total_count : null);
        setHasNextPage(result.next_cursor != null);
        // Store cursor for the next page so forward navigation avoids OFFSET
        if (result.next_cursor != null) {
          cursorMapRef.current[pageNum + 1] = result.next_cursor;
        }
      }
    } catch (err) {
      console.error('Error fetching houses:', err);
      setError(err.message || 'Failed to fetch houses');
    } finally {
      setLoading(false);
    }
  }, [activeSearch]);

  useEffect(() => {
    fetchPage(page);
  }, [page, fetchPage]);

  useEffect(() => {
    if (buyerId) fetchFavorites();
  }, [buyerId, fetchFavorites]);

  // Auto-trigger semantic search when initialSemanticQuery is provided
  useEffect(() => {
    if (initialSemanticQuery) {
      cursorMapRef.current = { 1: null };
      setActiveSearch({ mode: SEARCH_MODES.SEMANTIC, query: initialSemanticQuery });
      setPage(1);
      setHasNextPage(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCriteriaChange = (event) => {
    const { name, value } = event.target;
    setCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async () => {
    if (criteriaInvalid) return;
    if (searchMode === SEARCH_MODES.SEMANTIC) {
      if (!semanticQuery.trim()) {
        setActiveSearch(null);
        cursorMapRef.current = { 1: null };
      } else {
        setActiveSearch({ mode: SEARCH_MODES.SEMANTIC, query: semanticQuery.trim() });
      }
    } else {
      const hasCriteria = Object.values(criteria).some((value) => value !== '');
      if (hasCriteria) {
        setActiveSearch({ mode: SEARCH_MODES.CRITERIA, criteria });
      } else {
        setActiveSearch(null);
        cursorMapRef.current = { 1: null };
      }
    }
    setHasNextPage(false);
    setPage(1);
    searchPanelRef.current?.close();
  };

  const handleReset = () => {
    setCriteria(initialCriteria);
    setSemanticQuery('');
    setActiveSearch(null);
    cursorMapRef.current = { 1: null };
    setHasNextPage(false);
    setPage(1);
  };

  const handleSaveSearch = async () => {
    if (!saveName.trim()) return;
    if (criteriaInvalid) {
      setSaveError(Object.values(rangeErrors)[0]);
      return;
    }
    setSaveError(null);
    try {
      let searchCriteria;
      if (searchMode === SEARCH_MODES.SEMANTIC) {
        searchCriteria = { search_type: 'semantic', query: semanticQuery.trim() };
      } else {
        searchCriteria = { search_type: 'criteria' };
        Object.entries(criteria).forEach(([key, value]) => {
          if (value !== '') searchCriteria[key] = value;
        });
      }
      await createSearch(saveName.trim(), searchCriteria);
      setSaveSuccess(true);
      setSaveName('');
      setIsSavingSearch(false);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      console.error('Failed to save search:', err);
      setSaveError(err.message || 'Failed to save search');
    }
  };

  const goToPage = (newPage) => {
    if (newPage < 1) {
      return;
    }

    if (hasKnownTotal) {
      if (newPage > totalPages) {
        return;
      }
      setPage(newPage);
      return;
    }

    // Unknown total mode (default browse): allow back navigation, and forward
    // only by one page when backend reported next_cursor.
    if (newPage <= page || (newPage === page + 1 && hasNextPage)) {
      setPage(newPage);
    }
  };

  const handleOpenTourModal = (house) => {
    setSelectedHouseForTour(house);
    setTourModalOpen(true);
  };

  const handleCloseTourModal = () => {
    setTourModalOpen(false);
    setSelectedHouseForTour(null);
  };

  const handleTourSuccess = () => {
    handleCloseTourModal();
  };

  const toggleFavorite = async (house) => {
    const isFavorited = isFavorite(house.id);
    try {
      if (isFavorited) {
        await removeFavorite(house.id);
      } else {
        await addFavorite(house.id, `Interested in property at ${house.address}`);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  if (loading && houses.length === 0) {
    return (
      <div className="houses-container">
        <div className="houses-grid">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="house-card house-card--skeleton" aria-hidden="true">
              <div className="skeleton-img" />
              <div className="skeleton-line" />
              <div className="skeleton-line skeleton-line--short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="houses-container">
        <div className="error">
          Error: {error}
          <p className="error-hint">Check your connection or try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="houses-container">
      <h2>Available Houses</h2>

      <SearchPanel
        ref={searchPanelRef}
        title="Search & Filter"
        regionLabel="Property search filters"
        meta={
          <span className="search-panel__mode-pill">
            {searchMode === SEARCH_MODES.CRITERIA ? 'Criteria' : 'Semantic'}
          </span>
        }
        chips={filterChips}
        onChipDismiss={handleRemoveFilter}
        badgeCount={activeFilterCount}
      >
          <div className="search-mode-toggle" role="radiogroup" aria-label="Search mode">
            <button
              role="radio"
              aria-checked={searchMode === SEARCH_MODES.CRITERIA}
              className={`search-mode-toggle__option ${searchMode === SEARCH_MODES.CRITERIA ? 'search-mode-toggle__option--active' : ''}`}
              onClick={() => setSearchMode(SEARCH_MODES.CRITERIA)}
            >Criteria Search</button>
            <button
              role="radio"
              aria-checked={searchMode === SEARCH_MODES.SEMANTIC}
              className={`search-mode-toggle__option ${searchMode === SEARCH_MODES.SEMANTIC ? 'search-mode-toggle__option--active' : ''}`}
              onClick={() => setSearchMode(SEARCH_MODES.SEMANTIC)}
            >Semantic Search</button>
          </div>

          {searchMode === SEARCH_MODES.CRITERIA ? (
            <div className="criteria-grid">
              <div className="criteria-field">
                <label htmlFor="search-city">City</label>
                <input id="search-city" type="text" name="city" value={criteria.city} onChange={handleCriteriaChange} placeholder="City" />
              </div>
              <div className="criteria-field">
                <label htmlFor="search-state">State</label>
                <input id="search-state" type="text" name="state" value={criteria.state} onChange={handleCriteriaChange} placeholder="e.g. TX" maxLength={2} />
              </div>
              <div className="criteria-field">
                <label htmlFor="search-min-price">Min Price</label>
                <input id="search-min-price" type="number" name="min_price" value={criteria.min_price} onChange={handleCriteriaChange} placeholder="Min price" />
              </div>
              <div className="criteria-field">
                <label htmlFor="search-max-price">Max Price</label>
                <input
                  id="search-max-price"
                  type="number"
                  name="max_price"
                  value={criteria.max_price}
                  onChange={handleCriteriaChange}
                  placeholder="Max price"
                  aria-invalid={rangeErrors.max_price ? true : undefined}
                  aria-describedby={rangeErrors.max_price ? 'search-max-price-error' : undefined}
                />
                <FieldError id="search-max-price-error" message={rangeErrors.max_price} />
              </div>
              <div className="criteria-field">
                <label htmlFor="search-min-beds">Min Bedrooms</label>
                <input id="search-min-beds" type="number" name="min_bedrooms" value={criteria.min_bedrooms} onChange={handleCriteriaChange} placeholder="Min beds" />
              </div>
              <div className="criteria-field">
                <label htmlFor="search-min-full-baths">Min full baths</label>
                <input
                  id="search-min-full-baths"
                  type="number"
                  inputMode="numeric"
                  name="min_full_baths"
                  min="0"
                  max="20"
                  step="1"
                  value={criteria.min_full_baths}
                  onChange={handleCriteriaChange}
                  placeholder="e.g. 2"
                  aria-describedby="search-min-full-baths-hint"
                  data-testid="search-min-full-baths"
                />
                <span
                  id="search-min-full-baths-hint"
                  className="criteria-field__hint"
                >
                  Excludes legacy listings without a bath breakdown.
                </span>
              </div>
            </div>
          ) : (
            <div className="semantic-row">
              <div className="semantic-field">
                <span className="semantic-field__icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                  </svg>
                </span>
                <input
                  type="text"
                  value={semanticQuery}
                  onChange={(event) => setSemanticQuery(event.target.value)}
                  placeholder="Try: modern home with pool near downtown"
                  aria-label="Describe your ideal property"
                />
              </div>
            </div>
          )}

          <div className="search-actions">
            <button type="button" className="search-actions__primary" onClick={handleSearch} disabled={criteriaInvalid}>Search</button>
            <button type="button" className="search-actions__secondary" onClick={handleReset}>Reset</button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="search-actions__save"
                onClick={() => { setIsSavingSearch(true); setSaveError(null); }}
                aria-label="Save this search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save
              </button>
            )}
          </div>

          {isSavingSearch && (
            <div className="save-search-inline">
              <input
                type="text"
                className="save-search-inline__input"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSearch(); if (e.key === 'Escape') setIsSavingSearch(false); }}
                placeholder="Name this search..."
                aria-label="Saved search name"
                autoFocus
                maxLength={80}
              />
              <button
                type="button"
                className="save-search-inline__confirm"
                onClick={handleSaveSearch}
                disabled={!saveName.trim()}
              >
                Save
              </button>
              <button
                type="button"
                className="save-search-inline__cancel"
                onClick={() => { setIsSavingSearch(false); setSaveName(''); setSaveError(null); }}
              >
                Cancel
              </button>
              {saveError && <span className="save-search-inline__error">{saveError}</span>}
            </div>
          )}

          {saveSuccess && (
            <div className="save-search-success" role="status">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Search saved!
            </div>
          )}
      </SearchPanel>

      {houses.length === 0 ? (
        <div className="no-houses">No houses found for the current search.</div>
      ) : (
        <>
          <div className="results-summary">
            {hasKnownTotal
              ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} houses`
              : `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + houses.length}${hasNextPage ? ' (more available)' : ''}`}
          </div>
          <div className="houses-grid">
            {houses.map((house) => {
            const favorited = isFavorite(house.id);
            const images = Array.isArray(house.image_urls) ? house.image_urls : [];
            const propertyFeatures = extractPropertyFeatures(house);
            return (
              <div key={house.id} className="house-card">
                <div className="house-image-container">
                  {images.length > 0 ? (
                    <button
                      className="house-image-btn"
                      onClick={() => onViewHouse && onViewHouse(house.id)}
                      aria-label="View property details"
                    >
                      <img
                        className="house-image"
                        src={images[0]}
                        alt={`Primary view of ${house.address}`}
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <button
                      className="house-no-image"
                      onClick={() => onViewHouse && onViewHouse(house.id)}
                      aria-label="View property details"
                    >
                      No Image
                    </button>
                  )}
                  {isSold(house.listing_status) && (
                    <span className="house-status-overlay">
                      <Badge variant="listing" value="sold" />
                    </span>
                  )}
                </div>

                <div className="house-content">
                  <button
                    className="house-address-btn"
                    onClick={() => onViewHouse && onViewHouse(house.id)}
                  >
                    {house.address}
                  </button>

                  <p className="house-location">
                    {house.listing_number && <span className="listing-badge">#{house.listing_number}</span>}
                    {house.city}, {house.state} {house.zip_code}
                  </p>

                  {isVacantLand(house) ? (
                  <div className="house-features">
                    <span className="feature" data-testid={`land-label-${house.id}`}>{VACANT_LAND_LABEL}</span>
                  </div>
                  ) : (
                  <div className="house-features">
                    <span className="feature">
                      <strong>{house.bedrooms || 'N/A'}</strong> Beds
                    </span>
                    <span className="feature feature--bath-breakdown">
                      <BathBreakdownBadge
                        full={house.full_baths}
                        threeQuarter={house.three_quarter_baths}
                        half={house.half_baths}
                        fallbackTotal={house.bathrooms}
                        size="sm"
                        testId={`bath-breakdown-badge-${house.id}`}
                      />
                    </span>
                    <span className="feature">
                      <strong>{house.square_feet?.toLocaleString() || 'N/A'}</strong> Sq Ft
                    </span>
                  </div>
                  )}

                  <p className="house-price">${house.price?.toLocaleString() || 'N/A'}</p>

                  {house.ownership_documents_reviewed === true && (
                    <div className="house-trust-row">
                      <OwnershipReviewedBadge
                        reviewed
                        reviewedMonth={house.ownership_reviewed_month}
                        size="sm"
                      />
                    </div>
                  )}

                  {propertyFeatures.length > 0 && (
                    <div className="property-feature-tags">
                      {propertyFeatures.map((feature) => (
                        <span key={`${house.id}-${feature}`} className="property-feature-tag">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  {house.description && (
                    <p className="house-description">{house.description}</p>
                  )}

                  {house.score !== undefined && (
                    <p className="house-semantic-score">Match: {Math.round(house.score * 100)}%</p>
                  )}

                  <div className="house-actions">
                    <button
                      className="action-button view-button"
                      onClick={() => onViewHouse && onViewHouse(house.id)}
                      title="View details"
                      aria-label="View property details"
                    >
                      View Property
                    </button>
                    <button
                      className={`action-button favorite-button ${favorited ? 'favorited' : ''}`}
                      onClick={() => toggleFavorite(house)}
                      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <span aria-hidden="true">{favorited ? '❤️' : '🤍'}</span>
                      {favorited ? ' Saved' : ' Save'}
                    </button>
                    {/* QA T-29: only `active` / `pending` listings accept tour
                        requests (TOURABLE_LISTING_STATUSES in
                        backend/app/services/listing_visibility.py). The backend
                        enforces this with a 409; hiding the CTA avoids offering
                        an action that cannot succeed — e.g. on a `sold` card,
                        which stays browsable as a comp. A seller can't tour
                        their own listing either. */}
                    {isTourable(house.listing_status)
                      && !(buyerId != null && house.seller_id === buyerId) && (
                      <button
                        className="action-button tour-button"
                        onClick={() => handleOpenTourModal(house)}
                        title="Request a tour"
                        aria-label="Request a tour"
                      >
                        <span aria-hidden="true">🎫</span> Tour
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

          {hasKnownTotal && totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => goToPage(1)}
                disabled={page === 1 || loading}
                aria-label="First page"
              >
                «
              </button>
              <button
                className="pagination-button"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1 || loading}
                aria-label="Previous page"
              >
                ‹
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) {
                    acc.push('ellipsis-' + p);
                  }
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="pagination-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`pagination-button ${item === page ? 'active' : ''}`}
                      onClick={() => goToPage(item)}
                      disabled={loading}
                      aria-label={`Page ${item}`}
                      aria-current={item === page ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                className="pagination-button"
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages || loading}
                aria-label="Next page"
              >
                ›
              </button>
              <button
                className="pagination-button"
                onClick={() => goToPage(totalPages)}
                disabled={page === totalPages || loading}
                aria-label="Last page"
              >
                »
              </button>
            </div>
          )}

          {!hasKnownTotal && (page > 1 || hasNextPage) && (
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1 || loading}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="pagination-button active" aria-current="page">{page}</span>
              <button
                className="pagination-button"
                onClick={() => goToPage(page + 1)}
                disabled={!hasNextPage || loading}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
      
      {tourModalOpen && selectedHouseForTour && (
        <TourRequestModal
          buyerId={buyerId}
          houseId={selectedHouseForTour.id}
          houseName={selectedHouseForTour.address}
          onClose={handleCloseTourModal}
          onSuccess={handleTourSuccess}
        />
      )}
    </div>
  );
};

export default HousesComponent;
